import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useUsage } from "@/lib/usage";
import PacManBar from "@/pages/worth/PacManBar";

const MAX_POLLS = 15;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refresh } = useUsage();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 }, zIndex: 9999 });
          return;
        }
      } catch { /* keep polling */ }
      if (tries >= MAX_POLLS) setStatus("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="payment-success-page">
      {status === "checking" ? (
        <><PacManBar className="" testid="payment-checking-bar" />
          <p className="mt-4 text-sm text-slate-600" data-testid="payment-status-checking">Confirming your payment…</p></>
      ) : status === "paid" ? (
        <><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h1 className="mt-4 text-3xl font-bold tracking-tight" data-testid="payment-status-paid">Welcome to Pro!</h1>
          <p className="mt-2 text-sm text-slate-600">Unlimited AI drafts and full sales analytics are unlocked.</p></>
      ) : (
        <><XCircle className="mx-auto h-12 w-12 text-amber-500" />
          <h1 className="mt-4 text-2xl font-bold" data-testid="payment-status-pending">Still processing</h1>
          <p className="mt-2 text-sm text-slate-600">We couldn't confirm the payment yet. Check Plan &amp; Billing in a minute.</p></>
      )}
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/" className="rounded-full bg-[#0053DA] px-5 py-2 text-sm font-semibold text-white" data-testid="payment-go-create">Start drafting</Link>
        <Link to="/billing" className="rounded-full border border-slate-200 px-5 py-2 text-sm font-medium" data-testid="payment-go-billing">Plan &amp; Billing</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="payment-cancel-page">
      <XCircle className="mx-auto h-12 w-12 text-slate-400" />
      <h1 className="mt-4 text-2xl font-bold">Checkout cancelled</h1>
      <p className="mt-2 text-sm text-slate-600">No charge was made. You can upgrade anytime.</p>
      <Link to="/billing" className="mt-6 inline-block rounded-full bg-[#0053DA] px-5 py-2 text-sm font-semibold text-white" data-testid="payment-cancel-back">Back to Plan &amp; Billing</Link>
    </div>
  );
}
